import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef,
    type MRT_SortingState,
} from 'material-react-table';
import { isValid, parseISO } from 'date-fns';
import { formatAppDateTime } from '../utils/dateFormats';
import { MarkerHighlightTextField, MarkerTableText, hasTextMarkers, renderTextWithMarkers } from '../utils/textMarkerHighlight';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LaunchIcon from '@mui/icons-material/Launch';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { pathOpenerItemService, type PathOpenerItemPayload } from '../api/path-opener-items';
import { taskService } from '../api/tasks';
import { PathOpenerItem } from '../types/entities/path-opener-item.entity';
import type { PriorityOption } from '../types/entities/task.entity';

const MODE_OPTIONS = ['Auto', 'Browser', 'Explorer'] as const;

function normalizeMode(raw: string): string {
    const key = (raw || 'Auto').trim().toLowerCase();
    if (key === 'browser') return 'Browser';
    if (key === 'explorer') return 'Explorer';
    return 'Auto';
}

function formatDateSafe(value: string | null): string {
    if (!value) return '—';
    try {
        const d = parseISO(value);
        if (!isValid(d)) return '—';
        return formatAppDateTime(d);
    } catch {
        return '—';
    }
}

function parsePositionInput(raw: string): number | undefined {
    const t = raw.trim();
    if (t === '') return undefined;
    const n = Number(t);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : undefined;
}

function parsePriorityField(raw: string): number | undefined {
    const t = raw.trim();
    if (t === '') return undefined;
    const n = Number(t);
    return Number.isFinite(n) ? Math.floor(n) : undefined;
}

const isHttpUrl = (raw: string): boolean => /^https?:\/\//i.test((raw ?? '').trim());
const isWindowsPath = (raw: string): boolean => /^[A-Za-z]:\\/.test((raw ?? '').trim());
const toFileUrl = (windowsPath: string): string => {
    const trimmed = (windowsPath ?? '').trim();
    const encoded = trimmed
        .replace(/\\/g, '/')
        .split('/')
        .map((segment) => encodeURIComponent(segment))
        .join('/');
    return `file:///${encoded}`;
};

const queryKey = ['path-opener-catalog'];

const PATH_OPENER_SORT_STORAGE_KEY = 'path-opener.sort-preset.v1';

type PathOpenerSortPreset = 'priority' | 'updated';

const SORTING_BY_PRESET: Record<PathOpenerSortPreset, MRT_SortingState> = {
    priority: [
        { id: 'priority', desc: false },
        { id: 'position', desc: false },
    ],
    updated: [{ id: 'updated_at', desc: true }],
};

function readStoredSortPreset(): PathOpenerSortPreset {
    if (typeof window === 'undefined') {
        return 'priority';
    }
    const raw = window.localStorage.getItem(PATH_OPENER_SORT_STORAGE_KEY);
    return raw === 'updated' ? 'updated' : 'priority';
}

/** TaskPriorityEnum::MEDIUM — default for new entries */
const DEFAULT_PATH_OPENER_PRIORITY = 40;

function pathOpenerPayloadKey(payload: PathOpenerItemPayload): string {
    return JSON.stringify({
        value: (payload.value ?? '').trim(),
        name: payload.name ?? null,
        description: payload.description ?? null,
        mode: payload.mode ?? 'Auto',
        position: payload.position == null ? null : payload.position,
        priority: payload.priority ?? DEFAULT_PATH_OPENER_PRIORITY,
    });
}

function pathOpenerPayloadKeyFromRow(row: PathOpenerItem): string {
    const payload: PathOpenerItemPayload = {
        value: (row.value ?? '').trim(),
        name: (row.name ?? '').trim() || null,
        description: (row.description ?? '').trim() || null,
        mode: normalizeMode(row.mode ?? 'Auto'),
        position: typeof row.position === 'number' ? row.position : undefined,
        priority: row.priority?.value ?? DEFAULT_PATH_OPENER_PRIORITY,
    };
    return pathOpenerPayloadKey(payload);
}

type PathOpenerSearchMode = 'contains' | 'fuzzy' | 'startsWith';

function fuzzySubsequence(haystack: string, needle: string): boolean {
    const n = needle.trim().toLowerCase();
    if (!n) return true;
    const h = haystack.toLowerCase();
    let hi = 0;
    for (let i = 0; i < n.length; i++) {
        const ch = n[i];
        const idx = h.indexOf(ch, hi);
        if (idx === -1) return false;
        hi = idx + 1;
    }
    return true;
}

/** Primary: path/URL (`value`). Also light match on id + mode (contains only). */
function rowMatchesPathOpenerSearch(row: PathOpenerItem, rawQuery: string, mode: PathOpenerSearchMode): boolean {
    const q = rawQuery.trim();
    if (!q) return true;

    const valueRaw = row.value ?? '';
    const valueLower = valueRaw.toLowerCase();
    const valueNorm = valueLower.replace(/\\/g, '/');
    const qLower = q.toLowerCase();
    const qNorm = qLower.replace(/\\/g, '/');

    let valueOk = false;
    if (mode === 'startsWith') {
        valueOk = valueLower.startsWith(qLower) || valueNorm.startsWith(qNorm);
    } else if (mode === 'fuzzy') {
        valueOk = fuzzySubsequence(valueRaw, q) || fuzzySubsequence(valueNorm, qNorm);
    } else {
        valueOk = valueLower.includes(qLower) || valueNorm.includes(qNorm);
    }

    if (valueOk) return true;

    const nameRaw = row.name ?? '';
    const descRaw = row.description ?? '';
    let metaOk = false;
    if (mode === 'startsWith') {
        metaOk =
            nameRaw.toLowerCase().startsWith(qLower) ||
            descRaw.toLowerCase().startsWith(qLower);
    } else if (mode === 'fuzzy') {
        metaOk = fuzzySubsequence(nameRaw, q) || fuzzySubsequence(descRaw, q);
    } else {
        metaOk =
            nameRaw.toLowerCase().includes(qLower) ||
            descRaw.toLowerCase().includes(qLower);
    }
    if (metaOk) return true;

    const idOk = (row.id ?? '').toLowerCase().includes(qLower);
    const modeOk = normalizeMode(row.mode ?? '').toLowerCase().includes(qLower);
    return idOk || modeOk;
}

export const PathOpener: React.FC = () => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    const [sortPreset, setSortPreset] = useState<PathOpenerSortPreset>(() => readStoredSortPreset());
    const [sorting, setSorting] = useState<MRT_SortingState>(() => SORTING_BY_PRESET[readStoredSortPreset()]);

    const applySortPreset = useCallback((preset: PathOpenerSortPreset) => {
        setSortPreset(preset);
        setSorting(SORTING_BY_PRESET[preset]);
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(PATH_OPENER_SORT_STORAGE_KEY, preset);
        }
    }, []);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [formValue, setFormValue] = useState('');
    const [formName, setFormName] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [formMode, setFormMode] = useState<string>('Auto');
    const [formPosition, setFormPosition] = useState('');
    const [formPriority, setFormPriority] = useState(String(DEFAULT_PATH_OPENER_PRIORITY));
    const [taskPriorities, setTaskPriorities] = useState<PriorityOption[]>([]);
    const [valueError, setValueError] = useState('');

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<PathOpenerItem | null>(null);

    const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
    const [isClosingModal, setIsClosingModal] = useState(false);
    const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autoSaveResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastSavedPayloadKeyRef = useRef<string>('');

    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [searchMode, setSearchMode] = useState<PathOpenerSearchMode>('contains');

    useEffect(() => {
        document.title = t('pathOpener.title');
    }, [t]);

    useEffect(() => {
        let cancelled = false;
        void taskService.getEnums().then((priorities) => {
            if (!cancelled) setTaskPriorities(priorities);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const catalogQuery = useQuery({
        queryKey,
        queryFn: () => pathOpenerItemService.getCatalog(),
    });

    const buildPathPayload = useCallback((): PathOpenerItemPayload | null => {
        const trimmed = formValue.trim();
        if (!trimmed) {
            return null;
        }
        return {
            value: trimmed,
            name: formName.trim() || null,
            description: formDescription.trim() || null,
            mode: normalizeMode(formMode),
            position: parsePositionInput(formPosition),
            priority: parsePriorityField(formPriority) ?? DEFAULT_PATH_OPENER_PRIORITY,
        };
    }, [formValue, formName, formDescription, formMode, formPosition, formPriority]);

    const closeDialogImmediately = useCallback(() => {
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
        }
        if (autoSaveResetTimerRef.current) {
            clearTimeout(autoSaveResetTimerRef.current);
            autoSaveResetTimerRef.current = null;
        }
        setAutoSaveStatus('idle');
        setIsClosingModal(false);
        setDialogOpen(false);
        setDialogMode('create');
        setEditingId(null);
        setFormValue('');
        setFormName('');
        setFormDescription('');
        setFormMode('Auto');
        setFormPosition('');
        setFormPriority(String(DEFAULT_PATH_OPENER_PRIORITY));
        setValueError('');
        lastSavedPayloadKeyRef.current = '';
    }, []);

    const createMutation = useMutation({
        mutationFn: (payload: PathOpenerItemPayload) => pathOpenerItemService.create(payload),
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: PathOpenerItemPayload }) =>
            pathOpenerItemService.update(id, payload),
    });

    const persistEntry = useCallback(
        async (options: { closeOnSuccess: boolean; showSuccessToast: boolean; isAutoSave: boolean }): Promise<boolean> => {
            const payload = buildPathPayload();
            if (!payload) {
                if (!options.isAutoSave) {
                    setValueError(t('validation.required'));
                }
                return false;
            }

            if (createMutation.isPending || updateMutation.isPending) {
                return false;
            }

            const payloadKey = pathOpenerPayloadKey(payload);
            if (options.isAutoSave && payloadKey === lastSavedPayloadKeyRef.current) {
                return true;
            }

            const wasCreate = !editingId;

            if (options.isAutoSave) {
                setAutoSaveStatus('saving');
            }

            try {
                let saved: PathOpenerItem;
                if (editingId) {
                    saved = await updateMutation.mutateAsync({ id: editingId, payload });
                } else {
                    saved = await createMutation.mutateAsync(payload);
                }

                lastSavedPayloadKeyRef.current = payloadKey;

                await queryClient.invalidateQueries({ queryKey });

                if (!options.closeOnSuccess) {
                    setEditingId(saved.id);
                    setDialogMode('edit');
                }

                if (options.isAutoSave) {
                    setAutoSaveStatus('saved');
                    if (autoSaveResetTimerRef.current) {
                        clearTimeout(autoSaveResetTimerRef.current);
                    }
                    autoSaveResetTimerRef.current = setTimeout(() => setAutoSaveStatus('idle'), 1200);
                }

                if (options.showSuccessToast) {
                    enqueueSnackbar(
                        wasCreate ? t('pathOpener.messages.createSuccess') : t('pathOpener.messages.updateSuccess'),
                        { variant: 'success' },
                    );
                }

                if (options.closeOnSuccess) {
                    closeDialogImmediately();
                }

                return true;
            } catch (err: unknown) {
                if (options.isAutoSave) {
                    setAutoSaveStatus('error');
                } else {
                    enqueueSnackbar(err instanceof Error ? err.message : t('common.errorSavingData'), {
                        variant: 'error',
                    });
                }
                return false;
            }
        },
        [
            buildPathPayload,
            closeDialogImmediately,
            createMutation,
            editingId,
            enqueueSnackbar,
            queryClient,
            t,
            updateMutation,
        ],
    );

    const requestCloseModal = useCallback(async () => {
        if (isClosingModal) {
            return;
        }
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
        }

        const payload = buildPathPayload();
        const key = payload ? pathOpenerPayloadKey(payload) : '';
        const dirty = Boolean(payload && key !== lastSavedPayloadKeyRef.current);

        if (dirty) {
            setIsClosingModal(true);
            const saved = await persistEntry({
                closeOnSuccess: false,
                showSuccessToast: false,
                isAutoSave: true,
            });
            setIsClosingModal(false);
            if (!saved) {
                enqueueSnackbar(
                    t('pathOpener.messages.autosaveBeforeCloseFailed', {
                        defaultValue: 'Failed to save before close.',
                    }),
                    { variant: 'error' },
                );
                return;
            }
        }

        closeDialogImmediately();
    }, [buildPathPayload, closeDialogImmediately, enqueueSnackbar, isClosingModal, persistEntry, t]);

    useEffect(() => {
        if (!dialogOpen) {
            return;
        }

        const payload = buildPathPayload();
        if (!payload) {
            return;
        }

        const key = pathOpenerPayloadKey(payload);
        if (key === lastSavedPayloadKeyRef.current) {
            return;
        }

        if (createMutation.isPending || updateMutation.isPending) {
            return;
        }

        setAutoSaveStatus('pending');
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }

        autoSaveTimerRef.current = setTimeout(() => {
            void persistEntry({ isAutoSave: true, closeOnSuccess: false, showSuccessToast: false });
        }, 900);

        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dialogOpen,
        formValue,
        formName,
        formDescription,
        formMode,
        formPosition,
        formPriority,
        editingId,
        createMutation.isPending,
        updateMutation.isPending,
        persistEntry,
    ]);

    useEffect(() => {
        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
            if (autoSaveResetTimerRef.current) {
                clearTimeout(autoSaveResetTimerRef.current);
            }
        };
    }, []);

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => pathOpenerItemService.delete(id),
        onSuccess: async () => {
            enqueueSnackbar(t('pathOpener.messages.deleteSuccess'), { variant: 'success' });
            setDeleteOpen(false);
            setDeleteTarget(null);
            await queryClient.invalidateQueries({ queryKey });
        },
        onError: () => {
            enqueueSnackbar(t('pathOpener.messages.deleteError'), { variant: 'error' });
        },
    });

    const openCreate = () => {
        setDialogMode('create');
        setEditingId(null);
        setFormValue('');
        setFormName('');
        setFormDescription('');
        setFormMode('Auto');
        setFormPosition('');
        setFormPriority(String(DEFAULT_PATH_OPENER_PRIORITY));
        setValueError('');
        lastSavedPayloadKeyRef.current = '';
        setAutoSaveStatus('idle');
        setDialogOpen(true);
    };

    const openEdit = (row: PathOpenerItem) => {
        setDialogMode('edit');
        setEditingId(row.id);
        setFormValue(row.value ?? '');
        setFormName(row.name ?? '');
        setFormDescription(row.description ?? '');
        setFormMode(normalizeMode(row.mode ?? 'Auto'));
        setFormPosition(row.position != null ? String(row.position) : '');
        setFormPriority(String(row.priority?.value ?? DEFAULT_PATH_OPENER_PRIORITY));
        setValueError('');
        lastSavedPayloadKeyRef.current = pathOpenerPayloadKeyFromRow(row);
        setAutoSaveStatus('idle');
        setDialogOpen(true);
    };

    const handleSaveDialog = () => {
        if (!formValue.trim()) {
            setValueError(t('validation.required'));
            return;
        }
        setValueError('');
        void persistEntry({ closeOnSuccess: true, showSuccessToast: true, isAutoSave: false });
    };

    const isAutoSaveBusy =
        isClosingModal ||
        autoSaveStatus === 'pending' ||
        autoSaveStatus === 'saving' ||
        createMutation.isPending ||
        updateMutation.isPending;

    const copyToClipboard = useCallback(async (text: string) => {
        const value = (text ?? '').trim();
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            enqueueSnackbar('Copied to clipboard', { variant: 'success', autoHideDuration: 1200 });
        } catch {
            enqueueSnackbar('Copy failed', { variant: 'error' });
        }
    }, [enqueueSnackbar]);

    const openPathOpenerValue = useCallback((raw: string) => {
        const value = (raw ?? '').trim();
        if (!value) return;

        if (isHttpUrl(value)) {
            window.open(value, '_blank', 'noopener,noreferrer');
            return;
        }

        if (isWindowsPath(value)) {
            // Best-effort: browsers may open Explorer for file:// URLs on Windows, but it's not guaranteed.
            window.open(toFileUrl(value), '_blank', 'noopener,noreferrer');
            return;
        }

        // Unknown scheme: best effort open
        window.open(value, '_blank', 'noopener,noreferrer');
    }, []);

    const handlePathCellActivate = useCallback(
        (raw: string) => {
            const value = (raw ?? '').trim();
            if (!value) {
                return;
            }
            if (isHttpUrl(value)) {
                openPathOpenerValue(value);
            } else {
                void copyToClipboard(value);
            }
        },
        [copyToClipboard, openPathOpenerValue],
    );

    const columns = useMemo<MRT_ColumnDef<PathOpenerItem>[]>(
        () => [
            {
                accessorKey: 'value',
                header: t('pathOpener.columns.value'),
                size: 420,
                minSize: 280,
                enableColumnFilter: false,
                Cell: ({ row }) => {
                    const value = (row.original.value ?? '').trim();
                    if (!value) {
                        return (
                            <Typography variant="body2" color="text.secondary">
                                —
                            </Typography>
                        );
                    }
                    const isUrl = isHttpUrl(value);
                    const hint = isUrl
                        ? t('pathOpener.pathClickOpen', { defaultValue: 'Open link' })
                        : t('pathOpener.pathClickCopy', { defaultValue: 'Copy path' });
                    const display = value.length > 72 ? `${value.slice(0, 72)}…` : value;
                    return (
                        <Tooltip title={`${hint}: ${value}`}>
                            <Box
                                component="button"
                                type="button"
                                data-path-opener-path
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handlePathCellActivate(value);
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 0.75,
                                    width: '100%',
                                    maxWidth: '100%',
                                    p: 0,
                                    m: 0,
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    color: 'primary.main',
                                    font: 'inherit',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                {isUrl ? (
                                    <LaunchIcon sx={{ fontSize: 18, mt: 0.15, flexShrink: 0 }} />
                                ) : (
                                    <ContentCopyIcon sx={{ fontSize: 18, mt: 0.15, flexShrink: 0 }} />
                                )}
                                <Box
                                    component="span"
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        minWidth: 0,
                                    }}
                                >
                                    {hasTextMarkers(display) ? renderTextWithMarkers(display, 'table') : display}
                                </Box>
                            </Box>
                        </Tooltip>
                    );
                },
            },
            {
                accessorKey: 'id',
                header: t('pathOpener.columns.id'),
                size: 120,
                enableEditing: false,
            },
            {
                accessorKey: 'name',
                header: t('pathOpener.columns.name'),
                size: 160,
                Cell: ({ cell }) => {
                    const v = (cell.getValue<string | null>() ?? '').trim();
                    return v ? <MarkerTableText text={v} /> : '—';
                },
            },
            {
                accessorKey: 'description',
                header: t('pathOpener.columns.description'),
                size: 220,
                Cell: ({ cell }) => {
                    const v = (cell.getValue<string | null>() ?? '').trim();
                    if (!v) {
                        return '—';
                    }
                    const short = v.length > 80 ? `${v.slice(0, 80)}…` : v;
                    return (
                        <Tooltip title={v}>
                            <span>
                                {hasTextMarkers(short) ? renderTextWithMarkers(short, 'table') : short}
                            </span>
                        </Tooltip>
                    );
                },
            },
            {
                accessorKey: 'mode',
                header: t('pathOpener.columns.mode'),
                size: 110,
                Cell: ({ cell }) => normalizeMode(cell.getValue<string>() ?? ''),
            },
            {
                id: 'priority',
                accessorFn: (row) => row.priority?.value ?? DEFAULT_PATH_OPENER_PRIORITY,
                header: t('pathOpener.columns.priority'),
                size: 140,
                Cell: ({ row }) => row.original.priority?.label ?? '—',
            },
            {
                accessorKey: 'position',
                header: t('pathOpener.columns.position'),
                size: 100,
            },
            {
                accessorKey: 'updated_at',
                header: t('pathOpener.columns.updatedAt'),
                size: 160,
                Cell: ({ cell }) => formatDateSafe(cell.getValue<string | null>()),
                sortingFn: 'datetime',
            },
        ],
        [t, handlePathCellActivate]
    );

    const entries = catalogQuery.data ?? [];

    const filteredEntries = useMemo(
        () => entries.filter((row) => rowMatchesPathOpenerSearch(row, globalFilter, searchMode)),
        [entries, globalFilter, searchMode],
    );

    const prioritySelectOptions: PriorityOption[] =
        taskPriorities.length > 0
            ? taskPriorities
            : [{ value: DEFAULT_PATH_OPENER_PRIORITY, label: 'Medium' }];

    const table = useMaterialReactTable({
        columns,
        data: filteredEntries,
        getRowId: (row) => row.id,
        initialState: {
            columnPinning: { right: ['mrt-row-actions'] },
            showGlobalFilter: false,
        },
        state: {
            sorting,
            isLoading: catalogQuery.isLoading,
            showAlertBanner: catalogQuery.isError,
        },
        onSortingChange: setSorting,
        enableGlobalFilter: false,
        enableRowActions: true,
        positionActionsColumn: 'last',
        muiToolbarAlertBannerProps: catalogQuery.isError
            ? {
                  color: 'error',
                  children: t('common.errorFetchingData'),
              }
            : undefined,
        muiTableBodyRowProps: ({ row }) => ({
            onClick: (event: React.MouseEvent<HTMLTableRowElement>) => {
                if (event.button !== 0) {
                    return;
                }
                const el = event.target as HTMLElement;
                if (el.closest('button, a, input, textarea, select, [role="checkbox"], [data-path-opener-path]')) {
                    return;
                }
                openEdit(row.original);
            },
            sx: { cursor: 'pointer' },
        }),
        renderTopToolbarCustomActions: () => (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                    {t('pathOpener.create')}
                </Button>
                <Tooltip title={t('common.refreshData')}>
                    <IconButton onClick={() => catalogQuery.refetch()} disabled={catalogQuery.isFetching}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="path-opener-search-mode-label">{t('pathOpener.searchMode')}</InputLabel>
                    <Select
                        labelId="path-opener-search-mode-label"
                        label={t('pathOpener.searchMode')}
                        value={searchMode}
                        onChange={(e) => setSearchMode(e.target.value as PathOpenerSearchMode)}
                    >
                        <MenuItem value="contains">{t('pathOpener.searchModeContains')}</MenuItem>
                        <MenuItem value="fuzzy">{t('pathOpener.searchModeFuzzy')}</MenuItem>
                        <MenuItem value="startsWith">{t('pathOpener.searchModeStartsWith')}</MenuItem>
                    </Select>
                </FormControl>
            </Stack>
        ),
        renderRowActions: ({ row }) => (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Copy">
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void copyToClipboard(row.original.value);
                        }}
                    >
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={isHttpUrl(row.original.value) ? 'Open in new tab' : 'Try open via file:// (Windows, best effort)'}>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openPathOpenerValue(row.original.value);
                        }}
                    >
                        {isHttpUrl(row.original.value) ? <LaunchIcon fontSize="small" /> : <FolderOpenIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('common.edit')}>
                    <IconButton
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openEdit(row.original);
                        }}
                    >
                        <EditIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('pathOpener.deleteTitle')}>
                    <IconButton
                        color="error"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDeleteTarget(row.original);
                            setDeleteOpen(true);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        ),
    });

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>
                {t('pathOpener.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('pathOpener.description')}
            </Typography>
            <TextField
                fullWidth
                label={t('common.search', { defaultValue: 'Search' })}
                placeholder={t('pathOpener.searchPlaceholder')}
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                inputRef={searchInputRef}
                sx={{ mb: 2 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                        </InputAdornment>
                    ),
                    endAdornment: globalFilter ? (
                        <InputAdornment position="end">
                            <Tooltip title={t('common.clear', { defaultValue: 'Clear' })}>
                                <IconButton
                                    size="small"
                                    edge="end"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                        setGlobalFilter('');
                                        requestAnimationFrame(() => searchInputRef.current?.focus());
                                    }}
                                    aria-label="Clear search"
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    ) : undefined,
                }}
            />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                    {t('pathOpener.quickFilters', { defaultValue: 'Quick filters' })}
                </Typography>
                <Chip
                    label={t('pathOpener.sortByPriority', { defaultValue: 'Priority' })}
                    size="small"
                    clickable
                    color={sortPreset === 'priority' ? 'primary' : 'default'}
                    variant={sortPreset === 'priority' ? 'filled' : 'outlined'}
                    onClick={() => applySortPreset('priority')}
                />
                <Chip
                    label={t('pathOpener.sortByUpdated', { defaultValue: 'Updated' })}
                    size="small"
                    clickable
                    color={sortPreset === 'updated' ? 'primary' : 'default'}
                    variant={sortPreset === 'updated' ? 'filled' : 'outlined'}
                    onClick={() => applySortPreset('updated')}
                />
            </Box>

            <MaterialReactTable table={table} />

            <Dialog
                open={dialogOpen}
                onClose={() => {
                    void requestCloseModal();
                }}
                disableEscapeKeyDown={isAutoSaveBusy}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>{dialogMode === 'create' ? t('pathOpener.createTitle') : t('pathOpener.editTitle')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <MarkerHighlightTextField
                            label={t('pathOpener.columns.name')}
                            value={formName}
                            onChange={(e) => setFormName(e.target.value)}
                            fullWidth
                            inputProps={{ maxLength: 255 }}
                            helperText={t('pathOpener.remarkHint')}
                        />
                        <MarkerHighlightTextField
                            label={t('pathOpener.columns.value')}
                            value={formValue}
                            onChange={(e) => {
                                setFormValue(e.target.value);
                                if (valueError) setValueError('');
                            }}
                            error={Boolean(valueError)}
                            helperText={valueError}
                            fullWidth
                            multiline
                            minRows={2}
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                                        <Tooltip title="Copy">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={!formValue.trim()}
                                                    onClick={() => void copyToClipboard(formValue)}
                                                    aria-label="Copy path"
                                                >
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip
                                            title={
                                                isHttpUrl(formValue)
                                                    ? 'Open in new tab'
                                                    : 'Try open via file:// (Windows, best effort)'
                                            }
                                        >
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={!formValue.trim()}
                                                    onClick={() => openPathOpenerValue(formValue)}
                                                    aria-label="Open path"
                                                >
                                                    {isHttpUrl(formValue) ? (
                                                        <LaunchIcon fontSize="small" />
                                                    ) : (
                                                        <FolderOpenIcon fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <MarkerHighlightTextField
                            label={t('pathOpener.columns.description')}
                            value={formDescription}
                            onChange={(e) => setFormDescription(e.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="path-opener-mode-label">{t('pathOpener.columns.mode')}</InputLabel>
                            <Select
                                labelId="path-opener-mode-label"
                                label={t('pathOpener.columns.mode')}
                                value={formMode}
                                onChange={(e) => setFormMode(e.target.value)}
                            >
                                {MODE_OPTIONS.map((m) => (
                                    <MenuItem key={m} value={m}>
                                        {m}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth required>
                            <InputLabel id="path-opener-priority-label">{t('pathOpener.columns.priority')}</InputLabel>
                            <Select
                                labelId="path-opener-priority-label"
                                label={t('pathOpener.columns.priority')}
                                value={formPriority}
                                onChange={(e) => setFormPriority(String(e.target.value))}
                            >
                                {prioritySelectOptions.map((p) => (
                                    <MenuItem key={p.value} value={String(p.value)}>
                                        {p.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {t('pathOpener.priorityHint')}
                            </Typography>
                        </FormControl>
                        <TextField
                            label={t('pathOpener.columns.position')}
                            value={formPosition}
                            onChange={(e) => setFormPosition(e.target.value)}
                            fullWidth
                            type="number"
                            inputProps={{ min: 0 }}
                            helperText={t('pathOpener.positionHint')}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    {dialogOpen && (
                        <Typography
                            variant="caption"
                            sx={{
                                mr: 'auto',
                                px: 1.25,
                                py: 0.4,
                                borderRadius: 1,
                                fontWeight: 600,
                                color: autoSaveStatus === 'error' ? 'error.dark' : 'text.primary',
                                bgcolor:
                                    autoSaveStatus === 'pending'
                                        ? 'warning.light'
                                        : autoSaveStatus === 'saving'
                                            ? 'info.light'
                                            : autoSaveStatus === 'saved'
                                                ? 'success.light'
                                                : autoSaveStatus === 'error'
                                                    ? 'error.light'
                                                    : 'action.hover',
                            }}
                        >
                            {autoSaveStatus === 'pending' && 'Autosave pending...'}
                            {autoSaveStatus === 'saving' && 'Autosaving...'}
                            {autoSaveStatus === 'saved' && 'Saved ✓'}
                            {autoSaveStatus === 'error' && 'Autosave failed'}
                            {autoSaveStatus === 'idle' && 'Autosave ready'}
                        </Typography>
                    )}
                    <Button
                        onClick={() => {
                            void requestCloseModal();
                        }}
                        disabled={isAutoSaveBusy}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button variant="contained" onClick={handleSaveDialog} disabled={isAutoSaveBusy}>
                        {editingId ? t('common.save') : t('common.create')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteOpen} onClose={() => !deleteMutation.isPending && setDeleteOpen(false)}>
                <DialogTitle>{t('pathOpener.deleteTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('pathOpener.deleteConfirmation')}</Typography>
                    {deleteTarget && (
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                            {(deleteTarget.name ?? '').trim() ? (
                                <Typography variant="subtitle2">{deleteTarget.name}</Typography>
                            ) : null}
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {deleteTarget.value}
                            </Typography>
                            {(deleteTarget.description ?? '').trim() ? (
                                <Typography variant="body2" color="text.secondary">
                                    {deleteTarget.description}
                                </Typography>
                            ) : null}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)} disabled={deleteMutation.isPending}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
                        disabled={deleteMutation.isPending}
                    >
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
